'use strict';

const { SVG } = require('@svgdotjs/svg.js');

/**
 * SVG Model
 */
class SvgModel {

	/**
	 * Constructor
	 */
	constructor(svg) {
		this.components = [];

		this.updateSvg(svg);
	}

	/**
	 * Get SVG
	 * @returns SVG
	 */
	getSvg() {
		return this.svg.svg();
	}

	/**
	 * Get X
	 * @returns {*}
	 */
	getX() {
		return this.svg.x();
	}

	/**
	 * Set X
	 * @param x X
	 */
	setX(x) {
		this.svg.x(x);
		return this;
	}

	/**
	 * Get Y
	 * @returns {*}
	 */
	getY() {
		return this.svg.y();
	}

	/**
	 * Set Y
	 * @param y Y
	 */
	setY(y) {
		this.svg.y(y);
		return this;
	}

	/**
	 * Get Width
	 * @returns {*}
	 */
	getWidth() {
		return this.svg.width();
	}

	/**
	 * Set Width
	 * @param w Width
	 */
	setWidth(w) {
		this.svg.width(w);
		return this;
	}

	/**
	 * Get Height
	 * @returns {*}
	 */
	getHeight() {
		return this.svg.height();
	}

	/**
	 * Set Height
	 * @param h Height
	 */
	setHeight(h) {
		this.svg.height(h);
		return this;
	}

	/**
	 * Get View Box
	 * @returns {*}
	 */
	getViewbox() {
		return this.svg.viewbox();
	}

	/**
	 * Set View Box
	 *
	 * @param x X
	 * @param y Y
	 * @param w Width
	 * @param h Height
	 */
	setViewbox(x, y, w, h) {
		if (typeof x === 'string') {
			this.svg.viewbox(x);
		} else {
			this.svg.viewbox(x, y, w, h);
		}
		return this;
	}

	/**
	 * Set CSS
	 * @param styles CSS object
	 */
	setStyles(styles) {
		this.svg.attr('style', styles);

		return this;
	}

	/**
	 * Get CSS
	 * @returns {*}
	 */
	getCss() {
		return this.svg.css();
	}

	/**
	 * Update SVG
	 * @param svg SVG
	 */
	updateSvg(svg) {
		this.svg = SVG(svg);

		for (const component in this.components) {
			this.updateComponent(this.components[component]);
		}
	}

	/**
	 * Insert SVG Model
	 * @param svgModel SVG Model
	 * @param data Additional Data
	 */
	insertSvg(svgModel, data) {

		const group = this.svg.group();

		group.add(svgModel.svg);

		if (data && data.additionalStyles) {
			group.attr('style', data.additionalStyles)
		}
	}

	/**
	 * Inserts the SVG defs element into the SVG Model.
	 * @returns None
	 */
	insertDefs() {
		this.svg.defs();
	}

	/**
	 * Inserts a pattern element.
	 *
	 * @param {Object} attributes - to be assigned to the pattern
	 * @param {string | number} [width=100%] attributes.width - The value for the width attribute
	 * @param {string | number} [height=100%] attributes.height - The value for the height attribute
	 * @param {string | number} [x=0] attributes.x - The value for the x attribute
	 * @param {string | number} [y=0] attributes.y - The value for the y attribute
	 * @param {string} attributes.id - The value for the id attribute
	 * @param {string} [patternUnits=null] attributes.patterUnits - The value for the patternUnits attribute
	 * @param {Object} attributes.viewBox - Object representing the viewBox attribute
	 * @param {Object} [x] attributes.viewBox.x - The x value to be set in the viewBox attribute
	 * @param {Object} [y] attributes.viewBox.y - The y value to be set in the viewBox attribute
	 * @param {Object} [width] attributes.viewBox.width - The width value to be set in the viewBox attribute
	 * @param {Object} [height] attributes.viewBox.height - The height value to be set in the viewBox attribute
	 * @returns pattern object
	 */
	insertPattern({
		width = "100%",
		height = "100%",
		x = 0,
		y = 0,
		id,
		patternUnits = null,
		viewBox,
	} = {}) {
		const pattern = this.svg.pattern(width, height);
		pattern.attr({ x, y, id, patternUnits });

		if (viewBox) {
			if (viewBox.x === undefined || viewBox.y === undefined || viewBox.width === undefined || viewBox.height === undefined)
				throw new Error('viewBox object not correctly formatted');
			pattern.viewbox(viewBox);
		}
		return pattern;
	}

	/**
	 * Insert an image into the SVG model or other element identified by elementId.
	 * @param {Object} attributes
	 * @param {string} attributes.href - The value for the image's href attribute
	 * @param {string | number} attributes.x - The value for the image's x attribute
	 * @param {string | number} attributes.y - The value for the image's y attribute
	 * @param {string | number} attributes.width - The value for the image's width attribute
	 * @param {string | number} attributes.height - The value for the image's height attribute
	 * @param {string | number} attributes.elementId - The id of the element to which the image will be injected
	 * @param {string | number} attributes.element - The SVG element to which the image will be injected
	 * @returns None
	 */
	insertImage({ href, x, y, width, height, elementId, element }) {
		let elementTarget = element || this.svg;
		if (elementId) {
			elementTarget = this.svg.findOne(`#${elementId}`)
			if (!elementTarget)
				throw new Error(`Element with id '${elementId}' doesn't exist in the SVG.`)
		}
		const image = elementTarget.image();
		image.attr({ x, y, width, height, href });
	}

	/**
	 * Update Component
	 *
	 * @param action Action to update component
	 * @param component_name Component Name from Recipe
	 * @param component Component Object from Recipe
	 * @param options Component update options
	 */
	updateComponent({ action, component_name, component, options }) {
		this.components[component_name] = {
			action,
			component_name,
			component,
			options
		};

		this[action](component_name, component, options);
	}
}

SvgModel.prototype.updateComponentFill = require('./methods/updateComponentFill');
SvgModel.prototype.updateComponentPattern = require('./methods/updateComponentPattern');
SvgModel.prototype.updateComponentUGC = require('./methods/updateComponentUGC');

module.exports = SvgModel;

